import {
  VideoShareUserListResult,
  VideoShareUserUpdate,
} from "api/interfaces/videoshareInterface.interface";
import { deleteUserVideoShare, updateUserVideoShare } from "api/videoshareAPI";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import EditVSPresenter from "./EditVSPresenter";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { getDefaultExpires } from "../add/AddVSContainer";

type Props = {
  videoId: string;
  state: VideoShareUserListResult;
  onChangeModal: (type: string, value: boolean) => void;
};

export default function EditVSContainer({
  videoId,
  state,
  onChangeModal,
}: Props): JSX.Element {
  const { handleErrorNotify, lang, notifySuccess } = useApiError();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [isExpired, setIsExpired] = useState<boolean>(
    state.expires !== undefined ? true : false
  );

  const [newState, setNewState] = useState<VideoShareUserUpdate>({
    id: state.id,
    message: state.message,
    expires:
      state.expires !== undefined
        ? state.expires
        : getDefaultExpires(selectedAccount.timezone),
    canDownload: state.canDownload,
    shareDate: state.shareDate,
  });

  useEffect(() => {
    if (state) {
      setNewState({
        id: state.id,
        message: state.message,
        expires:
          state.expires !== undefined
            ? state.expires
            : getDefaultExpires(selectedAccount.timezone),
        canDownload: state.canDownload,
        shareDate: state.shareDate,
      });
    }
  }, [state]);

  const onChangeValues = (e: any) => {
    let {
      target: { name, value, checked },
    } = e;

    if (name === "isExpired") {
      setIsExpired(checked);
      return;
    }
    if (name === "canDownload") {
      setNewState({
        ...newState,
        [name]: checked,
      });
      return;
    }
    setNewState({
      ...newState,
      [name]: value,
    });
  };
  const onChangeDateValue = (date: string) => {
    // console.log(date);
    setNewState({
      ...newState,
      expires: date,
    });
  };

  const queryClient = useQueryClient();

  const mutationUpdateUser = useMutation(updateUserVideoShare, {
    onSuccess: () => {
      notifySuccess(lang.label.videoshare.notify.user.update.success);
      queryClient.invalidateQueries("videoshareViewUserList");
      onChangeModal("edit", false);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.videoshare.notify.user.update.fail);
    },
  });

  const onFinish = () => {
    mutationUpdateUser.mutate({
      videoId: videoId,
      accountId: selectedAccount.accountId,
      payload: { ...newState, expires: isExpired ? newState.expires : null },
    });
  };

  const mutationDeleteUser = useMutation(deleteUserVideoShare, {
    onSuccess: () => {
      notifySuccess(lang.label.videoshare.notify.user.delete.success);
      queryClient.invalidateQueries("videoshareViewUserList");
      onChangeModal("edit", false);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.videoshare.notify.user.delete.fail);
    },
  });

  const onClickDelete = useCallback(() => {
    mutationDeleteUser.mutate({
      userId: state.id,
    });
  }, [mutationDeleteUser, state.id]);

  return (
    <EditVSPresenter
      selectedAccount={selectedAccount}
      onChangeModal={onChangeModal}
      onChangeValues={onChangeValues}
      onChangeDateValue={onChangeDateValue}
      onFinish={onFinish}
      state={state}
      newState={newState}
      isExpired={isExpired}
      onClickDelete={onClickDelete}
    />
  );
}
